import { FC, useCallback, useEffect, useState } from "react";
import { DialogContent, DialogTitle, StepLabel, Step, Stepper, Grid, StepContent, Button, IconButton, DialogActions, CircularProgress } from "@mui/material";
import { Dialog } from "@mui/material";
import { LineItemDto, OrderReviewDto, OrderReviewLineItemDto } from "../../../../dtos";
import { OrderReviewSelectList } from "./OrderReviewSelectList";
import { OrderReviewLineItemSelectList } from "./OrderReviewLineItemSelectList";
import { CancelOutlined } from "@mui/icons-material";
import theme from "../../../../Theme";
import { QuoteLineItemSelectList } from "../QuoteLineItemSelectList";
import { useLineItemConverter } from "../../../../util";
import { useCreateOrderReviewAddLineItemToOrderReviewMutation } from "../../../../store/generated/generatedApi";

export interface ISelectOrderReviewLineItemStepperDialog {
    open: boolean;
    onClose: () => void;
    onComplete: (orderReview: OrderReviewDto, lineItem: OrderReviewLineItemDto) => void;
    date: Date;
}

export const SelectOrderReviewLineItemStepperDialog: FC<ISelectOrderReviewLineItemStepperDialog> = (props) => {
    const { open, onClose, onComplete, date } = props;

    const [orderReview, setOrderReview] = useState<OrderReviewDto | null>(null);
    const [activeStep, setActiveStep] = useState(0);
    const { createNewOrderReviewLineItemFromQuoteLineItem } = useLineItemConverter(orderReview?.quote);

    const [createOrderReviewLineItem, { data, isLoading: isCreating, isSuccess, reset }] = useCreateOrderReviewAddLineItemToOrderReviewMutation();

    const handleSelectionComplete = useCallback((orderReviewLineItem) => {
        onComplete(orderReview!, orderReviewLineItem);
        onClose();
        setActiveStep(0);
        setOrderReview(null);
        reset();
    }, [onComplete, onClose, reset, orderReview]);

    useEffect(() => {
        if (isSuccess) {
            handleSelectionComplete(data);
        }
    }, [isSuccess, data, handleSelectionComplete]);

    const handleAddNewOrderReviewLineItem = useCallback(
        async (quoteLineItem?: LineItemDto) => {
            if (orderReview) {
                var newOrderReviewLineItem = await createNewOrderReviewLineItemFromQuoteLineItem(quoteLineItem, orderReview);
                createOrderReviewLineItem(newOrderReviewLineItem);
            }
        },
        [createNewOrderReviewLineItemFromQuoteLineItem, createOrderReviewLineItem, orderReview]
    );

    return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>Select Order Review Line Item</Grid>
                <Grid item>
                    <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.primary.contrastText }}>
                        <CancelOutlined />
                    </IconButton>
                </Grid>
            </Grid>
        </DialogTitle>
        <DialogContent>
            <Grid container pt={2} spacing={2} direction='column' height='100%'>
                <Grid item height='100%'>
                    <Stepper
                        activeStep={activeStep}
                        orientation="vertical"
                        >
                        <Step key={'order-review'}>
                            <StepLabel>Select Order Review</StepLabel>
                            <StepContent>
                                <OrderReviewSelectList 
                                    date={date} 
                                    onSelect={(orderReview) => {
                                        setActiveStep(1);
                                        setOrderReview(orderReview);
                                    }} />
                            </StepContent>
                        </Step>
                        <Step key={'line-item'}>
                            <StepLabel>Select Line Item</StepLabel>
                            <StepContent>
                                <Grid container direction='column' spacing={2}>
                                    <Grid item>
                                        {orderReview && 
                                        <OrderReviewLineItemSelectList 
                                            orderReviewId={orderReview.id} 
                                            onSelect={handleSelectionComplete}
                                            onAddNewLineItem={() => {
                                                setActiveStep(2);
                                            }}
                                        /> }
                                    </Grid>
                                </Grid>
                            </StepContent>
                        </Step>
                        <Step key={'new-line-item'}>
                            <StepLabel>Add New Line Item</StepLabel>
                            <StepContent>
                                <Grid container direction='column' spacing={2}> 
                                    <Grid item>
                                        {orderReview && <QuoteLineItemSelectList
                                            quote={orderReview!.quote!}
                                            disableAddCustomLineItem={true}
                                            onAddQuoteLineItem={(quoteLineItem) => {
                                                handleAddNewOrderReviewLineItem(quoteLineItem);
                                            }}
                                            onAddCustomLineItem={() => {}}
                                        />}
                                    </Grid>
                                </Grid>
                            </StepContent>
                        </Step>
                    </Stepper>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            {isCreating && <CircularProgress />}
            {activeStep > 0 && <Button disabled={isCreating} variant='contained' onClick={() => {
                setActiveStep(activeStep - 1);
            }}>Back</Button>}
            {activeStep === 0 && <Button disabled={isCreating} variant='contained' onClick={onClose}>Close</Button>}
        </DialogActions>
    </Dialog>
    );
}

